import objectPath from 'object-path';
import { connect } from 'react-redux';
import React, { useEffect, useRef } from 'react';

import {
  updateOrder,
  addressSearch,
  addressesNextPage,
  addressesResetPage,
} from 'actions/orders';

import { errorInputClear } from 'actions/error';
import { addressSelector, globalxAddressSelector, } from 'selectors/order-create';

import Card from 'components/common/Card';
import RadioButton from 'components/RadioButton';
import LoadingSpinner from 'components/LoadingSpinner';
import useSkipConfirmDialog from '../useSkipConfirmDialog';
import { ADDRESS_SEARCH_ERROR } from 'constants/actions';

import {
  Button,
  SkipCard,
  StepTitle,
  PagesContain,
  ContentContain,
  StepInformation,
  LoadingContain,
  AddressesContain,
  ResultsContain,
  AddressTitle,
  SkipContain
} from '../CreateStyled';

import { stepAddressConfirm as handleValidate } from '../validation/';

function mapAddressDisplay (data = {}) {
  return [
    data.lotNumber ? `LOT ${data.lotNumber}` : '',
    data.unit ? `${data.unit} /` : '',
    data.number || '',
    data.street || '',
    data.streetType || '',
    data.otherLine1 || '',
    data.locality || '',
    data.state || '',
    data.postcode || ''
  ]
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();
}

const getPlanParcel = (address) => {
  if (address.lotPlan) {
    const pp      = address.lotPlan;
    const display = pp.planType && pp.planNumber ? `${pp.planType}${pp.planNumber}` : ``;
    
    return display;
  }

  if (address._embedded) {
    const planParcels = address._embedded.planParcels || [];
    const pp          = planParcels[0];
    const display     = pp ? `${pp.planType}${pp.planNumber} ${pp.parcelType} ${pp.parcelTypeDescription} ${pp.parcelNumber}` : '';
    return display;
  }

  return ``;
};

function mapAddressWA (address) {
  var display    = mapAddressDisplay(address.address);
  var planParcel = getPlanParcel(address);
  var titleRef   = address.titleReference;

  return [ `${titleRef}${planParcel}`, display, `${display} ${titleRef} ${planParcel}` ];
}

function mapAddressSA (address) {
  const volFolio      = objectPath.get(address, 'volumeFolio');
  const propLocations = objectPath.get(address, 'propertyLocations') || [];
  const planParcels   = objectPath.get(address, '_embedded.planParcels') || [];
  
  const pp             = planParcels[0];
  const addressDisplay = propLocations[0] || '';

  const planParcelDisplay = pp
    ? `${pp.planType}${pp.planNumber} ${pp.parcelType} ${pp.parcelTypeDescription} ${pp.parcelNumber}`
    : '';

  const details = volFolio
    ? `${volFolio.volFolPrefix}${volFolio.volume}/${volFolio.folio}`
    : '';

  const key = `${volFolio.volFolPrefix}${volFolio.volume}-${volFolio.folio}`;
  const detailsDisplay = `${addressDisplay} - ${details} - ${planParcelDisplay}`;

  return [ key, addressDisplay, detailsDisplay ]
}

export const StepAddressConfirm = ({
  loading,
  nextStep,
  nextPage,
  addresses,
  errorCode,
  resetPage,
  updateOrder,
  loadingPage,
  renderActions,
  order,
  fetchAddresses,
  selectedAddress,
  inputErrors,
  addressValues,
  onValueChange,
  onValidateAll
}) => {
  const scrollRef    = useRef(false);
  const addressCount = useRef(addresses.length);

  const defaultPageSize = 10;
  const isFullPage = (addresses.length % defaultPageSize) === 0;

  const isPagingEnabled = (
    errorCode !== ADDRESS_SEARCH_ERROR && (
    addressValues.address_state !== 'WA' && isFullPage
  ));

  useEffect(() => {
    if (!selectedAddress) {
      resetPage();
      fetchAddresses(true);
    }
  }, [ selectedAddress, resetPage, fetchAddresses ]);

  const showMore = () => {
    nextPage();
    fetchAddresses(false);
  }

  useEffect(
    function scrollOnPageChange () {
      if (isPagingEnabled) {
        if (addresses.length !== addressCount.current) {
          addressCount.current = addresses.length;
          
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        }
      }
    }, 
    [ isPagingEnabled, addresses.length ]
  );

  const handleNext = () => handleValidate({ selectedAddress });

  const handleChange = (address, addressDisplay) => {
    var data = address.address 
      ? address.address
      : address.propertyAddresses.filter(address => !!address.locality)[0]

    const updatedAddress = data && {
      address_lot:       data.lotNumber || '',
      address_unit:      data.unit || '',
      address_street_no: data.streetNumber || data.number || '',
      address_street_1:  `${data.street || ''} ${data.streetType || ''}`,
      address_street_2:  data.otherLine1 || '',
      address_suburb:    data.locality || '',
      address_state:     data.state || '',
      address_postcode:  data.postcode || addressValues.address_postcode || ''
    }; 

    updateOrder({
      ...(updatedAddress || {}),
      globalx_meta: address,
      globalx_address_display: addressDisplay,

      //clear strata values when changing address
      strata_name: undefined,
      strata_other: undefined,
      selected_strata: undefined,
      selected_strata_cert: undefined
    });
  };

  const onSkipSubmit = (values) => {
    updateOrder({
      ...values,
      order_title: false,
      globalx_meta: null,
      globalx_address_display: '',

      //clear strata values when changing address
      strata_name: undefined,
      strata_other: undefined,
      selected_strata: undefined,
      selected_strata_cert: undefined
    });

    nextStep();
  };

  const [ renderSkipDialog, toggleSkipDialog ] = useSkipConfirmDialog({
    onSubmit: onSkipSubmit,
    inputErrors,
    addressValues,
    onValueChange,
    onValidateAll
  });

  const onSkipConfirm = () => {
    toggleSkipDialog(true);
  };

  const renderAddresses = () => addresses.map((address) => {
    const mapAddress = addressValues.address_state === 'WA' ? mapAddressWA : mapAddressSA;
    const [ key, addressDisplay, addressDetails ] = mapAddress(address);

    return (
      <RadioButton
        key     = {key}
        name    = {addressDisplay}
        checked = {selectedAddress === key}
        label   = {addressDetails}
        onClick = {handleChange.bind(null, address, addressDetails )}
      />
    );
  });

  return (
    <React.Fragment>
      <SkipCard>
        <ContentContain>
          <SkipContain>
            <div>
              <StepTitle>Can't Find the Address?</StepTitle>
              <StepInformation>
                Provide additional property information in order to process this request.
              </StepInformation>
            </div>
            <Button
              secondary
              inline
              onClick={onSkipConfirm}
            >
              Manually Confirm
            </Button>
          </SkipContain>
        </ContentContain>
      </SkipCard>

      <Card.Content>
        <ContentContain>
          <StepTitle>Address Confirm</StepTitle>
          <StepInformation>
            Please confirm the address from the list below
          </StepInformation>

          {loading ? (
              <LoadingContain>
                <LoadingSpinner large />
                Finding Property Matches...
              </LoadingContain>
          ) : (
            <ResultsContain>
              <AddressTitle>Address Matches - Displaying {addresses.length} Results</AddressTitle>
                <AddressesContain ref={scrollRef}>
                  {renderAddresses()}
                </AddressesContain>
                
                <PagesContain>
                  {loadingPage
                    ? <LoadingContain justify="center"><LoadingSpinner /></LoadingContain>
                    : isPagingEnabled && (
                      <Button
                        fullWidth
                        secondary
                        removeTopRadius
                        onClick={showMore}
                      >
                        Show More
                      </Button>
                  )}
                </PagesContain>
              </ResultsContain>
            )}

          {!loading && renderActions({ disableNext: !selectedAddress, onNext: handleNext })}
        </ContentContain>
      </Card.Content>
      {renderSkipDialog()}
    </React.Fragment>
  );
};

StepAddressConfirm.defaultProps = {
  addresses: [],
  inputErrors: {},
  onValueChange: () => null,
  fetchAddresses: () => null
};

export default connect(
  (state) => ({
    errorCode: state.error.code,
    inputErrors: state.error.inputErrors,
    orderAddress: addressSelector(state),
    selectedAddress: globalxAddressSelector(state),
    loading: state.loading.loadingSearchAddress,
    addresses: state.pendingOrder.pending.title_addresses,
    addressPageLoading: state.loading.loadingAddressPage
  }),
  {
    dispatchOrderUpdate: updateOrder,
    dispatchAddressSearch: addressSearch,
    dispatchErrorInputClear: errorInputClear,
    dispatchAddressNextPage: addressesNextPage,
    dispatchAddressResetPage: addressesResetPage,
  }
  )(props =>
  <StepAddressConfirm
    {...props}
    addressValues={props.orderAddress}
    loadingPage={props.addressPageLoading}
    onValidateAll={props.onValidateAll}
    updateOrder={props.dispatchOrderUpdate}
    nextPage={props.dispatchAddressNextPage}
    resetPage={props.dispatchAddressResetPage}
    fetchAddresses={props.dispatchAddressSearch}
    onValueChange={props.dispatchErrorInputClear}
  />
);